import { Link, useParams, Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowUpRight, ArrowLeft, Check, Star, ChevronRight, CreditCard, Loader2, Zap, TrendingUp, AlertTriangle, Clock, Target } from "lucide-react";
import { servicesContent, servicesCategories } from "@/data/servicesContent";
import { buildSalesSections } from "@/data/serviceSalesCopy";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function ServiceDetail() {
  const { slug } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const svc = servicesContent[slug];

  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);

  if (!svc) return <Navigate to="/services" replace />;

  const startCheckout = async () => {
    if (!user) {
      toast.error("Sign in to purchase — redirecting...");
      setTimeout(() => { window.location.href = "/login"; }, 800);
      return;
    }
    setCheckoutBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: slug,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Checkout failed");
      setCheckoutBusy(false);
    }
  };

  const catMeta = servicesCategories[svc.category];
  const sales = buildSalesSections(svc);

  return (
    <div className="bg-[#0B0C10]" data-testid={`service-page-${slug}`}>
      {/* HERO */}
      <section className="relative border-b border-[#1F2833] py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 te-grid-bg-dense opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <nav className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7]/70">
            <Link to="/" className="hover:text-[#FF3355]">home</Link>
            <span className="mx-2">/</span>
            <Link to="/services" className="hover:text-[#FF3355]">services</Link>
            <span className="mx-2">/</span>
            <span className="text-[#FF3355]">{svc.name}</span>
          </nav>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8">
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C]">{svc.kicker}</div>
              <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.22em] text-[#C5C6C7]">{svc.categoryLabel}</div>
              <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[0.95]">
                {svc.name}
              </h1>
              <p className="mt-4 font-display text-xl text-[#FF3355]">{svc.tagline}</p>
              <p className="mt-6 text-[#C5C6C7] max-w-3xl leading-relaxed">{svc.intro}</p>
            </div>

            <aside className="lg:col-span-4 lg:sticky lg:top-24 lg:self-start">
              <div className="border border-[#1F2833] p-6 lg:p-7 bg-[#0d1117] relative">
                {svc.popular && (
                  <div className="absolute -top-3 left-6 inline-flex items-center gap-1.5 bg-emerald-400 text-[#0B0C10] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.22em] font-bold te-emerald-glow-sm">
                    <Star size={10} fill="currentColor" /> Most Popular
                  </div>
                )}
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7]">investment</div>
                <div className="mt-2 font-display text-3xl lg:text-4xl font-bold text-[#FF3355] leading-tight">
                  {svc.priceLabel}
                </div>
                <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[#C5C6C7]/70">
                  {svc.priceNote}
                </div>
                <Link
                  to={svc.ctaHref}
                  data-testid={`service-cta-${slug}`}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#B91C2C] text-[#F5F5F7] px-6 py-3 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355] transition-colors"
                >
                  [ {svc.cta} ] <ArrowUpRight size={14} />
                </Link>
                <button
                  onClick={startCheckout}
                  disabled={checkoutBusy}
                  data-testid={`service-checkout-${slug}`}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-[#FF3355] text-[#FF3355] hover:bg-[#FF3355] hover:text-[#0B0C10] px-6 py-3 font-mono text-xs uppercase tracking-[0.22em] font-bold transition-colors disabled:opacity-50"
                >
                  {checkoutBusy ? (<><Loader2 size={14} className="animate-spin" /> Redirecting</>) : (<><CreditCard size={14} /> Pay now with Stripe</>)}
                </button>
                <div className="mt-4 pt-4 border-t border-[#1F2833] font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7] space-y-1.5">
                  <div><span className="text-[#FF3355]">&gt;</span> Delivery — {svc.categoryLabel}</div>
                  <div><span className="text-[#FF3355]">&gt;</span> Founder-sponsored engagement</div>
                  <div><span className="text-[#FF3355]">&gt;</span> Portal-based collaboration</div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Who this is for */}
      <section className="py-16 lg:py-20 border-b border-[#1F2833]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C]">// WHO.THIS.IS.FOR</div>
            <h2 className="mt-4 font-display text-2xl lg:text-3xl font-bold tracking-tight">Fit criteria.</h2>
          </div>
          <div className="lg:col-span-8">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {svc.audience.map((a, i) => (
                <li key={i} className="te-lime-accent flex items-start gap-3 font-mono text-sm text-[#F5F5F7] border border-[#1F2833] p-4">
                  <span className="text-[#FF3355] mt-0.5">+</span>
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Included */}
      <section className="py-16 lg:py-20 border-b border-[#1F2833]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C]">// WHAT.INCLUDED</div>
          <h2 className="mt-4 font-display text-3xl lg:text-4xl font-bold tracking-tighter">Scope of work.</h2>
          <div className="mt-10 space-y-px bg-[#1F2833] border border-[#1F2833]">
            {svc.included.map((f, i) => (
              <div key={i} className="bg-[#0B0C10] p-6 grid grid-cols-1 lg:grid-cols-12 gap-4">
                <div className="lg:col-span-4 flex items-center gap-3">
                  <Check size={16} className="text-emerald-400 shrink-0" />
                  <div className="font-display text-lg font-semibold">{f.title}</div>
                </div>
                <div className="lg:col-span-8 text-sm text-[#C5C6C7] leading-relaxed">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 lg:py-20 border-b border-[#1F2833]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C]">// HOW.WE.DELIVER</div>
          <h2 className="mt-4 font-display text-3xl lg:text-4xl font-bold tracking-tighter">Delivery cadence.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#1F2833] border border-[#1F2833]">
            {svc.process.map((p) => (
              <div key={p.step} className="bg-[#0B0C10] p-6">
                <div className="font-display text-4xl text-[#FF3355] font-bold">{p.step}</div>
                <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.22em] text-[#C5C6C7]">{p.title}</div>
                <p className="mt-3 text-sm text-[#F5F5F7] leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables + Outcomes */}
      <section className="py-16 lg:py-20 border-b border-[#1F2833]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="border border-[#1F2833] p-8">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C]">// DELIVERABLES</div>
            <h3 className="mt-4 font-display text-2xl font-bold tracking-tight">What ships.</h3>
            <ul className="mt-6 space-y-3">
              {svc.deliverables.map((d, i) => (
                <li key={i} className="te-lime-accent flex items-start gap-3 font-mono text-sm text-[#F5F5F7]">
                  <span className="text-emerald-400 mt-0.5">→</span>{d}
                </li>
              ))}
            </ul>
          </div>
          <div className="border border-[#1F2833] p-8 bg-[#0d1117]">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C]">// OUTCOMES</div>
            <h3 className="mt-4 font-display text-2xl font-bold tracking-tight">What you should expect.</h3>
            <ul className="mt-6 space-y-3">
              {svc.outcomes.map((o, i) => (
                <li key={i} className="te-lime-accent flex items-start gap-3 text-sm text-[#F5F5F7] leading-relaxed">
                  <span className="text-[#FF3355] mt-0.5">↗</span>{o}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ============ WHY IT MATTERS ============ */}
      {sales?.whyItMatters && (
        <section className="py-16 lg:py-24 border-b border-[#1F2833]" data-testid="service-why-matters">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-400 mb-4 inline-flex items-center gap-2">
                <TrendingUp size={12} /> // WHY.THIS.MATTERS
              </div>
              <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tighter">
                {sales.whyItMatters.heading}
              </h2>
            </div>
            <div className="space-y-6 text-[#C5C6C7] leading-relaxed">
              <p className="text-base">{sales.whyItMatters.context}</p>
              <div className="border-l-2 border-[#FF3355] pl-5 py-2 bg-[#FF3355]/[0.03]">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF3355] mb-2 inline-flex items-center gap-1.5">
                  <AlertTriangle size={11} /> Cost of doing nothing
                </div>
                <p className="text-sm">{sales.whyItMatters.risk}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============ COST vs VALUE ============ */}
      {sales?.costValue && (
        <section className="py-16 lg:py-24 border-b border-[#1F2833]" data-testid="service-cost-value">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-400 mb-4 inline-flex items-center gap-2">
              <Target size={12} /> // COST.VS.VALUE
            </div>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tighter max-w-3xl">
              {sales.costValue.heading}
            </h2>

            <div className="mt-12 grid lg:grid-cols-3 gap-4">
              {/* Investment */}
              <div className="lg:col-span-1 bg-[#0d1117] border border-[#B91C2C]/60 p-8 flex flex-col">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B91C2C]">
                  your investment
                </div>
                <div className="mt-3 font-display text-5xl font-bold text-[#FF3355] leading-none">
                  {sales.costValue.yourInvestment}
                </div>
                {sales.costValue.investmentNote && (
                  <div className="mt-3 font-mono text-[11px] text-[#C5C6C7]/80 leading-relaxed">
                    {sales.costValue.investmentNote}
                  </div>
                )}
                <div className="mt-6 pt-6 border-t border-[#1F2833]">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7]/70 mb-2">
                    comparable market rates
                  </div>
                  <div className="text-xs text-[#C5C6C7] leading-relaxed line-through decoration-[#FF3355]/60">
                    {sales.costValue.comparable}
                  </div>
                </div>
              </div>

              {/* What's delivered */}
              <div className="lg:col-span-2 bg-[#0d1117] border border-emerald-400/60 p-8">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-400">
                  value delivered
                </div>
                <h3 className="mt-3 font-display text-2xl font-bold tracking-tight">
                  What you actually get.
                </h3>
                <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                  {sales.costValue.delivered.map((d, i) => (
                    <li
                      key={i}
                      className="te-lime-accent flex items-start gap-2 text-sm text-[#F5F5F7] leading-relaxed"
                    >
                      <Check size={14} className="text-emerald-400 mt-1 shrink-0" />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 pt-6 border-t border-[#1F2833] flex items-start gap-3">
                  <Zap size={16} className="text-emerald-400 mt-0.5 shrink-0" />
                  <p className="text-sm text-[#C5C6C7] font-mono">
                    {sales.costValue.payback}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============ WHY NOW ============ */}
      {sales?.whyNow && (
        <section className="py-16 lg:py-24 border-b border-[#1F2833]" data-testid="service-why-now">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-end">
              <div>
                <div className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-400 mb-4 inline-flex items-center gap-2">
                  <Clock size={12} /> // WHY.NOW
                </div>
                <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tighter max-w-3xl">
                  {sales.whyNow.heading}
                </h2>
                <p className="mt-6 text-[#C5C6C7] max-w-3xl leading-relaxed text-base">
                  {sales.whyNow.urgency}
                </p>
              </div>
              <div className="lg:min-w-[280px]">
                <div className="bg-[#0d1117] border border-emerald-400/60 p-6">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-400 mb-2">
                    typical timeline
                  </div>
                  <div className="font-display text-2xl font-bold text-[#F5F5F7]">
                    {sales.whyNow.timeline}
                  </div>
                </div>
                <Link
                  to={svc.ctaHref}
                  data-testid="why-now-cta"
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-emerald-400 text-[#0B0C10] px-5 py-3 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-emerald-300 transition-colors te-emerald-glow-sm"
                >
                  <Zap size={13} /> Start now
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      {svc.faqs?.length > 0 && (
        <section className="py-16 lg:py-20 border-b border-[#1F2833]">
          <div className="max-w-[900px] mx-auto px-6 lg:px-10">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C]">// FAQ</div>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tighter">Common questions.</h2>
            <div className="mt-10 space-y-px bg-[#1F2833] border border-[#1F2833]">
              {svc.faqs.map((f, i) => (
                <details key={i} className="bg-[#0B0C10] p-6 group" data-testid={`service-faq-${i}`}>
                  <summary className="cursor-pointer font-display text-lg font-semibold flex items-center justify-between gap-4 list-none">
                    <span>{f.q}</span>
                    <ChevronRight size={16} className="text-[#FF3355] group-open:rotate-90 transition-transform shrink-0" />
                  </summary>
                  <p className="mt-4 text-[#C5C6C7] leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related */}
      {svc.related?.length > 0 && (
        <section className="py-16 lg:py-20 border-b border-[#1F2833]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C]">// RELATED.SERVICES</div>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#1F2833] border border-[#1F2833]">
              {svc.related.map((rslug) => {
                const r = servicesContent[rslug];
                if (!r) return null;
                return (
                  <Link
                    key={rslug}
                    to={`/services/${rslug}`}
                    data-testid={`related-service-${rslug}`}
                    className="bg-[#0B0C10] p-6 te-glow-hover border border-transparent hover:border-[#B91C2C] group"
                  >
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B91C2C]">{r.categoryLabel}</div>
                    <h4 className="mt-3 font-display text-xl font-bold tracking-tight group-hover:text-[#FF3355] transition-colors">{r.name}</h4>
                    <p className="mt-2 font-mono text-xs text-[#FF3355]">{r.priceLabel}</p>
                    <p className="mt-3 text-sm text-[#C5C6C7] line-clamp-2">{r.tagline}</p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 border border-[#1F2833] bg-[#0d1117] p-8 lg:p-12">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-3">// NEXT.STEP</div>
            <h3 className="font-display text-2xl lg:text-4xl font-bold tracking-tight max-w-2xl">{svc.cta}.</h3>
            <p className="mt-3 text-[#C5C6C7] max-w-xl">Founder-sponsored scoping. No sales-team gauntlet.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/services"
              data-testid="service-back-overview"
              className="inline-flex items-center gap-2 border border-[#1F2833] hover:border-[#B91C2C] text-[#C5C6C7] hover:text-[#F5F5F7] px-6 py-4 font-mono text-xs uppercase tracking-[0.22em]"
            >
              <ArrowLeft size={14} /> all services
            </Link>
            <Link
              to={svc.ctaHref}
              className="inline-flex items-center gap-2 bg-[#B91C2C] text-[#F5F5F7] px-7 py-4 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355] transition-colors"
            >
              [ {svc.cta} ] <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
